/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * Standardized data-state components (W1.6 / #681).
 *
 * Every list/detail view eventually needs the same four states: loading,
 * error, empty, and (for perceived speed) a skeleton placeholder. Before this
 * module each page hand-rolled them — 23 pages carried a bespoke
 * `<Flex justify="center" py={12}><Spinner/></Flex>` and a one-off "No X found"
 * block, all subtly different. These components make the four states a single
 * import with consistent spacing, tone, and token usage (so they flip in dark
 * mode for free).
 *
 * The ergonomic win is {@link DataState}: pass the react-query-style
 * `isLoading` / `isError` / `isEmpty` flags and it renders the right state,
 * or the children when data is ready.
 */
import { isValidElement } from 'react';
import type { ReactNode } from 'react';
import { Box, Button, Flex, HStack, Skeleton, Spinner, Stack, Table, Text } from '@chakra-ui/react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

// ---------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------

export interface LoadingStateProps {
  /** Optional caption under the spinner. */
  label?: string;
  /** Vertical padding of the centering block. Defaults to 12 (matches pages). */
  py?: number;
  /** Minimum height — use for full-panel centering (e.g. "60vh"). */
  minH?: string;
}

/**
 * Centered spinner with an optional caption. Drop-in replacement for the
 * `<Flex justify="center" py={12}><Spinner color="accent.solid" /></Flex>`
 * block that was duplicated across pages.
 */
export const LoadingState = ({ label, py = 12, minH }: LoadingStateProps) => (
  <Flex direction="column" align="center" justify="center" gap={3} py={py} minH={minH}>
    <Spinner size="lg" color="accent.solid" borderWidth="3px" />
    {label && (
      <Text fontSize="sm" color="fg.muted">
        {label}
      </Text>
    )}
  </Flex>
);

// ---------------------------------------------------------------------------
// Error
// ---------------------------------------------------------------------------

export interface ErrorStateProps {
  /** Bold headline. Defaults to a generic failure title. */
  title?: string;
  /** Supporting detail — the caught error message, typically. */
  message?: string;
  /** When provided, renders a "Try again" button wired to this handler. */
  onRetry?: () => void;
  /** Override the retry button label. */
  retryLabel?: string;
  py?: number;
  minH?: string;
}

/**
 * Failure state — warning icon, title, optional detail, optional retry.
 * Use inside a card/panel where a full-page error boundary is too heavy.
 */
export const ErrorState = ({
  title = 'Something went wrong',
  message,
  onRetry,
  retryLabel = 'Try again',
  py = 12,
  minH,
}: ErrorStateProps) => (
  <Flex direction="column" align="center" justify="center" gap={2} py={py} minH={minH} textAlign="center">
    <Box color="error.solid">
      <AlertTriangle size={28} />
    </Box>
    <Text fontWeight={600} color="fg.title">
      {title}
    </Text>
    {message && (
      <Text fontSize="sm" color="fg.muted" maxW="480px">
        {message}
      </Text>
    )}
    {onRetry && (
      <Button size="sm" variant="outline" mt={2} onClick={onRetry}>
        <RefreshCw size={14} />
        {retryLabel}
      </Button>
    )}
  </Flex>
);

// ---------------------------------------------------------------------------
// Empty
// ---------------------------------------------------------------------------

export interface EmptyStateProps {
  /** Leading glyph — an emoji string or a lucide icon node. */
  icon?: ReactNode;
  /** Bold headline, e.g. "No users found". Keep it short — the state only. */
  title: string;
  /** Optional supporting line (what this surface is / how to populate it). */
  description?: string;
  /**
   * Standardized primary CTA — pass a label and a handler and the component
   * renders a consistent brand-primary button (`accent.solid`). This is the
   * preferred way to add a "create" affordance to an empty state; it keeps the
   * button identical across pages. Gate on permissions by passing
   * `onAction={canWrite ? handler : undefined}` — the button only renders when
   * both `actionLabel` and `onAction` are present.
   */
  actionLabel?: string;
  onAction?: () => void;
  /**
   * Fully custom call-to-action node. Use only when the standardized
   * `actionLabel`/`onAction` button doesn't fit; takes precedence over it.
   */
  action?: ReactNode;
  /**
   * Wrap the content in a bordered `bg.card` panel. Set on standalone empty
   * states so they match the ones rendered inside a table/list card. Omit when
   * the empty state already renders inside a card (avoids a double border).
   */
  bordered?: boolean;
  py?: number;
  minH?: string;
}

/**
 * Empty/no-results state — glyph, title, optional description, optional CTA.
 * Standardizes the assorted "No X yet" blocks pages rendered by hand.
 */
export const EmptyState = ({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  action,
  bordered = false,
  py = 12,
  minH,
}: EmptyStateProps) => {
  const cta =
    action ??
    (actionLabel && onAction ? (
      <Button size="sm" bg="accent.solid" color="white" _hover={{ bg: 'accent.ink' }} onClick={onAction}>
        {actionLabel}
      </Button>
    ) : null);

  const body = (
    <Flex direction="column" align="center" justify="center" gap={2} py={py} minH={minH} textAlign="center">
      {icon != null &&
        (typeof icon === 'string' ? (
          <Text fontSize="2xl" lineHeight={1}>
            {icon}
          </Text>
        ) : (
          <Box color="fg.muted">{icon}</Box>
        ))}
      <Text fontWeight={600} color="fg.default">
        {title}
      </Text>
      {description && (
        <Text fontSize="sm" color="fg.muted" maxW="480px">
          {description}
        </Text>
      )}
      {cta && <Box mt={2}>{cta}</Box>}
    </Flex>
  );

  if (bordered) {
    return (
      <Box bg="bg.card" border="1px solid" borderColor="border.default" borderRadius="12px">
        {body}
      </Box>
    );
  }
  return body;
};

// ---------------------------------------------------------------------------
// Skeletons
// ---------------------------------------------------------------------------

export interface TableSkeletonProps {
  /** Number of placeholder rows. Defaults to 5. */
  rows?: number;
  /** Number of columns per row. Defaults to 4. */
  columns?: number;
}

/**
 * Skeleton rows for a `<Table.Body>` while data loads — preserves table
 * layout (no reflow when real rows arrive), unlike a centered spinner.
 *
 * **It is a `<Table.Body>`, so it must be rendered inside a `<Table.Root>`** in place of the mapped
 * body. Anywhere else it throws `useTableStyles returned is 'undefined'` and takes the page with it.
 * In particular it is **not** a `DataState` `loading` node — that slot renders outside the table.
 */
export const TableSkeleton = ({ rows = 5, columns = 4 }: TableSkeletonProps) => (
  <Table.Body>
    {Array.from({ length: rows }).map((_, r) => (
      <Table.Row key={r}>
        {Array.from({ length: columns }).map((__, c) => (
          <Table.Cell key={c} px={4} py={3}>
            <Skeleton height="16px" borderRadius="sm" />
          </Table.Cell>
        ))}
      </Table.Row>
    ))}
  </Table.Body>
);

export interface CardSkeletonProps {
  /** Number of placeholder cards. Defaults to 3. */
  count?: number;
  /** Height of each card block. Defaults to "88px". */
  height?: string;
}

/**
 * Skeleton blocks for card/list layouts (non-table). Stacks `count` rounded
 * placeholders matching card height.
 */
export const CardSkeleton = ({ count = 3, height = '88px' }: CardSkeletonProps) => (
  <Stack gap={3}>
    {Array.from({ length: count }).map((_, i) => (
      <Skeleton key={i} height={height} borderRadius="lg" />
    ))}
  </Stack>
);

/** Inline row of skeleton stat tiles (e.g. above a chart). */
export const StatSkeleton = ({ count = 4 }: { count?: number }) => (
  <HStack gap={4} align="stretch">
    {Array.from({ length: count }).map((_, i) => (
      <Skeleton key={i} height="72px" flex="1" borderRadius="lg" />
    ))}
  </HStack>
);

// ---------------------------------------------------------------------------
// DataState — the one-line adoption path
// ---------------------------------------------------------------------------

export interface DataStateProps {
  /** True while the query is loading its first result. */
  isLoading?: boolean;
  /** True when the query failed. */
  isError?: boolean;
  /** True when the query succeeded but returned no rows. */
  isEmpty?: boolean;

  /** Error detail surfaced by {@link ErrorState}. */
  error?: unknown;
  /** Retry handler passed through to {@link ErrorState} (e.g. `refetch`). */
  onRetry?: () => void;

  /** Loading caption. */
  loadingLabel?: string;
  /**
   * Custom loading node, rendered **in place of** the children — so it must stand on its own.
   *
   * **Not `<TableSkeleton />`.** This said to pass one, and doing so throws: the skeleton is a bare
   * `<Table.Body>` and Chakra's table parts read a context only `<Table.Root>` provides, so it
   * crashes the page on mount rather than degrading. `TemplatesPage` followed this line and hit it
   * on its first render (`#1090`, 2026-08-25) — the recommendation was the defect, which is why it
   * is corrected here rather than only at the call site.
   *
   * To keep table layout while loading, render `<Table.Root>` and its header yourself and swap only
   * the body for `<TableSkeleton />`; `DataState` is outside the table, so it cannot do that for
   * you. Otherwise omit this and take the centered spinner, which is what every page does today.
   */
  loading?: ReactNode;

  /** Empty-state config (title required) or a fully custom node. */
  empty?: EmptyStateProps | ReactNode;

  /** Shared vertical padding for the built-in states. */
  py?: number;
  minH?: string;

  /** Rendered when data is present and non-empty. */
  children: ReactNode;
}

function isEmptyProps(v: EmptyStateProps | ReactNode): v is EmptyStateProps {
  return typeof v === 'object' && v !== null && !isValidElement(v) && 'title' in (v as object);
}

/**
 * Chooses the right state for a react-query-style resource:
 * `isLoading` → loading, `isError` → error, `isEmpty` → empty, else children.
 *
 * ```tsx
 * <DataState
 *   isLoading={isLoading}
 *   isError={isError}
 *   isEmpty={!rows.length}
 *   onRetry={refetch}
 *   empty={{ icon: '👥', title: 'No users found' }}
 * >
 *   <Table.Root>…</Table.Root>
 * </DataState>
 * ```
 */
export const DataState = ({
  isLoading,
  isError,
  isEmpty,
  error,
  onRetry,
  loadingLabel,
  loading,
  empty,
  py,
  minH,
  children,
}: DataStateProps) => {
  if (isLoading) {
    return <>{loading ?? <LoadingState label={loadingLabel} py={py} minH={minH} />}</>;
  }
  if (isError) {
    const message = error instanceof Error ? error.message : typeof error === 'string' ? error : undefined;
    return <ErrorState message={message} onRetry={onRetry} py={py} minH={minH} />;
  }
  if (isEmpty) {
    if (empty == null) {
      return <EmptyState title="Nothing here yet" py={py} minH={minH} />;
    }
    return isEmptyProps(empty) ? <EmptyState py={py} minH={minH} {...empty} /> : <>{empty}</>;
  }
  return <>{children}</>;
};
